/**
 * <predictive-search> custom element
 *
 * Wraps a search <form> and shows predictive results as the user types.
 * Reusable across header, search drawer, and search page — just place the
 * element around any search form that contains an input[name="q"].
 *
 * Usage:
 *   <predictive-search>
 *     <form action="/search" method="get">
 *       <input type="search" name="q" ...>
 *     </form>
 *   </predictive-search>
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._debounceTimer = null;
    this._isOpen = false;
    this._selectedIndex = -1;
  }

  connectedCallback() {
    this._form = this.querySelector('form');
    if (!this._form) return;

    this._input = this._form.querySelector('input[type="search"], input[name="q"]');
    if (!this._input) return;

    this._createDropdown();
    this._bindEvents();
  }

  disconnectedCallback() {
    this._unbindEvents();
    if (this._abortController) this._abortController.abort();
    clearTimeout(this._debounceTimer);
  }

  // ── DOM helpers ──────────────────────────────────────────────────────

  _createDropdown() {
    this._dropdown = document.createElement('div');
    this._dropdown.className = 'predictive-search__dropdown';
    this._dropdown.setAttribute('role', 'listbox');
    this._dropdown.setAttribute('aria-label', 'Search suggestions');
    this._dropdown.hidden = true;
    this.appendChild(this._dropdown);
  }

  // ── Event wiring ─────────────────────────────────────────────────────

  _bindEvents() {
    this._onInput = this._handleInput.bind(this);
    this._onKeyDown = this._handleKeyDown.bind(this);
    this._onFormSubmit = this._handleFormSubmit.bind(this);
    this._onClickOutside = this._handleClickOutside.bind(this);

    this._input.addEventListener('input', this._onInput);
    this._input.addEventListener('keydown', this._onKeyDown);
    this._form.addEventListener('submit', this._onFormSubmit);
    document.addEventListener('click', this._onClickOutside, true);
  }

  _unbindEvents() {
    if (this._input) {
      this._input.removeEventListener('input', this._onInput);
      this._input.removeEventListener('keydown', this._onKeyDown);
    }
    if (this._form) {
      this._form.removeEventListener('submit', this._onFormSubmit);
    }
    document.removeEventListener('click', this._onClickOutside, true);
  }

  // ── Handlers ─────────────────────────────────────────────────────────

  _handleInput() {
    const query = this._input.value.trim();
    this._selectedIndex = -1;

    if (!query) {
      this._close();
      return;
    }

    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._fetch(query), 200);
  }

  _handleKeyDown(event) {
    if (event.key === 'Escape') {
      if (this._isOpen) {
        event.preventDefault();
        this._close();
        this._input.blur();
      }
      return;
    }

    if (!this._isOpen) return;

    const items = this._dropdown.querySelectorAll('.predictive-search__item');
    if (!items.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this._selectedIndex = this._selectedIndex < items.length - 1 ? this._selectedIndex + 1 : 0;
        this._highlightItem(items);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this._selectedIndex = this._selectedIndex > 0 ? this._selectedIndex - 1 : items.length - 1;
        this._highlightItem(items);
        break;

      case 'Enter':
        if (this._selectedIndex >= 0) {
          event.preventDefault();
          const link = items[this._selectedIndex].getAttribute('href');
          if (link) window.location.href = link;
        }
        break;
    }
  }

  _handleFormSubmit(event) {
    // If a result is highlighted, navigate to it instead of submitting
    if (this._selectedIndex >= 0) {
      const items = this._dropdown.querySelectorAll('.predictive-search__item');
      const link = items[this._selectedIndex]?.getAttribute('href');
      if (link) {
        event.preventDefault();
        window.location.href = link;
      }
    }
  }

  _handleClickOutside(event) {
    if (!this.contains(event.target)) {
      this._close();
    }
  }

  // ── Keyboard highlight ───────────────────────────────────────────────

  _highlightItem(items) {
    items.forEach((item, i) => {
      const isActive = i === this._selectedIndex;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', String(isActive));
    });

    if (this._selectedIndex >= 0 && items[this._selectedIndex]) {
      items[this._selectedIndex].scrollIntoView({ block: 'nearest' });
    }

    this._input.focus();
  }

  // ── Fetch ────────────────────────────────────────────────────────────

  async _fetch(query) {
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();

    const url = new URL('/search/suggest.json', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const response = await fetch(url, { signal: this._abortController.signal });
      if (!response.ok) return;

      const data = await response.json();
      console.log('[PredictiveSearch] API response:', data.resources.results);
      this._renderResults(data.resources.results);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Predictive search error:', err);
      }
    }
  }

  // ── Render ───────────────────────────────────────────────────────────

  _renderResults(results) {
    const { products, collections, pages, articles, queries } = results;

    const hasProducts = products && products.length;
    const hasResources =
      (collections && collections.length) ||
      (pages && pages.length) ||
      (articles && articles.length) ||
      (queries && queries.length);

    if (!hasProducts && !hasResources) {
      this._close();
      return;
    }

    let html = '<div class="predictive-search__results-groups-wrapper">';

    if (hasProducts) {
      html += '<div class="predictive-search__result-group predictive-search__result-group--products">';
      html += '<p class="predictive-search__heading">Products</p>';
      html += '<ul class="predictive-search__list">';
      products.forEach((p) => {
        console.log(p.tags);
        const img = p.image
          ? '<img class="predictive-search__img" src="' + p.image + '" alt="" width="50" height="50" loading="lazy">'
          : '';
        const vendor = p.vendor ? '<span class="predictive-search__vendor">' + this._esc(p.vendor) + '</span>' : '';
        html +=
          '<li class="predictive-search__item" role="option" id="ps-product-' + p.id + '">' +
          '<a href="' + p.url + '" class="predictive-search__link" tabindex="-1">' +
          img +
          '<span class="predictive-search__text">' +
          '<span class="predictive-search__title">' + this._esc(p.title) + '</span>' +
          vendor +
          '</span>' +
          '</a>' +
          '</li>';
      });
      html += '</ul></div>';
    }

    if (hasResources) {
      html += '<div class="predictive-search__result-group predictive-search__result-group--resources">';

      if (collections && collections.length) {
        html += '<div class="predictive-search__group predictive-search__group--collections">';
        html += '<p class="predictive-search__heading">Collections</p>';
        html += '<ul class="predictive-search__list">';
        collections.forEach((c) => {
          html +=
            '<li class="predictive-search__item" role="option">' +
            '<a href="' + c.url + '" class="predictive-search__link" tabindex="-1">' +
            '<span class="predictive-search__text">' +
            '<span class="predictive-search__title">' + this._esc(c.title) + '</span>' +
            '</span>' +
            '</a>' +
            '</li>';
        });
        html += '</ul></div>';
      }

      if (pages && pages.length) {
        html += '<div class="predictive-search__group predictive-search__group--pages">';
        html += '<p class="predictive-search__heading">Pages</p>';
        html += '<ul class="predictive-search__list">';
        pages.forEach((pg) => {
          html +=
            '<li class="predictive-search__item" role="option">' +
            '<a href="' + pg.url + '" class="predictive-search__link" tabindex="-1">' +
            '<span class="predictive-search__text">' +
            '<span class="predictive-search__title">' + this._esc(pg.title) + '</span>' +
            '</span>' +
            '</a>' +
            '</li>';
        });
        html += '</ul></div>';
      }

      if (articles && articles.length) {
        html += '<div class="predictive-search__group predictive-search__group--articles">';
        html += '<p class="predictive-search__heading">Blog Posts</p>';
        html += '<ul class="predictive-search__list">';
        articles.forEach((a) => {
          const img = a.image
            ? '<img class="predictive-search__img" src="' + a.image + '" alt="" width="50" height="50" loading="lazy">'
            : '';
          html +=
            '<li class="predictive-search__item" role="option">' +
            '<a href="' + a.url + '" class="predictive-search__link" tabindex="-1">' +
            img +
            '<span class="predictive-search__text">' +
            '<span class="predictive-search__title">' + this._esc(a.title) + '</span>' +
            '</span>' +
            '</a>' +
            '</li>';
        });
        html += '</ul></div>';
      }

      if (queries && queries.length) {
        html += '<div class="predictive-search__group predictive-search__group--queries">';
        html += '<p class="predictive-search__heading">Queries</p>';
        html += '<ul class="predictive-search__list">';
        queries.forEach((q) => {
          html +=
            '<li class="predictive-search__item" role="option">' +
            '<a href="/search?q=' + encodeURIComponent(q.styled_text || q.text) + '" class="predictive-search__link" tabindex="-1">' +
            '<span class="predictive-search__text">' +
            '<span class="predictive-search__title">' + this._esc(q.styled_text || q.text) + '</span>' +
            '</span>' +
            '</a>' +
            '</li>';
        });
        html += '</ul></div>';
      }

      html += '</div>';
    }

    html += '</div>';

    html +=
      '<div class="predictive-search__footer">' +
      '<a href="/search?q=' + encodeURIComponent(this._input.value.trim()) + '" class="predictive-search__view-all">' +
      'View all results' +
      '</a>' +
      '</div>';

    this._dropdown.innerHTML = html;
    this._open();
  }

  // ── Open / Close ─────────────────────────────────────────────────────

  _open() {
    this._dropdown.hidden = false;
    this._isOpen = true;
    this._selectedIndex = -1;
    this._input.setAttribute('aria-expanded', 'true');
  }

  _close() {
    this._dropdown.hidden = true;
    this._isOpen = false;
    this._selectedIndex = -1;
    this._input.setAttribute('aria-expanded', 'false');
    clearTimeout(this._debounceTimer);
  }

  // ── Utility ──────────────────────────────────────────────────────────

  _esc(str) {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
